import type { ResolutionChangeRequest, ResolutionChangeStatus, ResolutionObservation } from '@playcastdotio/PeerCore';
import { ResolutionChangeVerifier } from './ResolutionChangeVerifier';
import { RESOLUTION_HOST_VERIFY_MS, RESOLUTION_RETRY_LIMIT } from '../../../../../../../../libs/PeerCore/src/streamRecovery/policy';

export interface ResolutionEffectPort {
  apply(request: ResolutionChangeRequest): Promise<void>;
  readSenderDimensions(): { width: number; height: number } | undefined;
}

/** Serializes effects and bounds reapplication; only observations can verify dimensions. */
export class MobileResolutionEnforcer {
  private active?: ResolutionChangeRequest;
  private task: Promise<void> = Promise.resolve();
  private attempts = 0;
  private disposed = false;
  private inFlight = false;

  constructor(private readonly deps: {
    effects: ResolutionEffectPort;
    verifier: ResolutionChangeVerifier;
    nowMs: () => number;
    emit: (status: ResolutionChangeStatus) => void;
  }) {}

  public request(request: ResolutionChangeRequest): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('resolution-enforcer-disposed'));
    if (this.active?.requestId === request.requestId) return this.task;
    if (this.active) this.deps.emit({ requestId: this.active.requestId, stage: 'superseded', elapsedMs: this.deps.nowMs() - this.active.requestedAtMs });
    this.active = request;
    this.attempts = 0;
    this.deps.emit(this.deps.verifier.request(request));
    this.task = this.task.catch(() => undefined).then(() => this.apply(request));
    return this.task;
  }

  public observe(observation: ResolutionObservation): void {
    if (this.disposed) return;
    const status = this.deps.verifier.observe(observation, this.deps.nowMs());
    if (status) this.deps.emit(status);
  }

  public tick(): void {
    if (this.disposed) return;
    const status = this.deps.verifier.tick(this.deps.nowMs());
    if (!status) return;
    this.deps.emit(status);
    // Sender/receiver verification timeouts never start a new capture budget.

  }

  public dispose(): void {
    this.disposed = true;
    this.active = undefined;
    this.deps.verifier.reset();
  }

  private async apply(request: ResolutionChangeRequest): Promise<void> {
    if (this.disposed || this.active !== request) return;
    const deadline = request.requestedAtMs + RESOLUTION_HOST_VERIFY_MS;
    if (this.deps.nowMs() >= deadline) {
      this.tick();
      throw new Error('Resolution host deadline exceeded');
    }
    this.inFlight = true;
    this.attempts += 1;
    try {
      await this.deps.effects.apply(request);
      if (!this.disposed && this.active === request) {
        if (this.deps.nowMs() >= deadline) { this.tick(); throw new Error('Resolution host deadline exceeded'); }
        this.deps.emit(this.deps.verifier.hostApplied(request.requestId, this.deps.nowMs()));
      }
    } catch (error) {
      if (this.disposed || this.active !== request) return;
      const captureFailure = error as { code?: string; retryable?: boolean } | null;
      if (captureFailure?.code === 'capture-preparation-failed' && captureFailure.retryable === true
        && this.attempts <= RESOLUTION_RETRY_LIMIT && this.deps.nowMs() < deadline) {
        this.inFlight = false;
        return this.apply(request);
      }
      if (this.deps.nowMs() >= deadline) this.tick();
      else this.deps.emit(this.deps.verifier.fail(request.requestId, error instanceof Error ? error.message : 'application-failed', this.deps.nowMs()));
      throw error;
    } finally {
      this.inFlight = false;
    }
  }
}
